import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Sparkles, Loader2, Building2, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card } from '../components/Shared';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = {
  free: ['Up to 10 documents / month', 'Quotations & Invoices', 'Basic PDF export', '1 user'],
  pro: ['Unlimited documents', 'All document types', 'WhatsApp & Email sharing', 'Payment tracking', 'Up to 5 users'],
  business: ['Everything in Pro', 'Custom branding & logo', 'Reports & analytics', 'LHDN e-Invoice ready', 'Unlimited users'],
};

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState('monthly');
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const checkout = async (lookupKey) => {
    setLoadingKey(lookupKey);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
      setLoadingKey(null);
    }
  };

  const planMeta = {
    pro: { icon: Rocket, tint: 'text-blue-600 bg-blue-100', highlight: true },
    business: { icon: Building2, tint: 'text-violet-600 bg-violet-100', highlight: false },
  };

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Plans & Billing" subtitle="Upgrade to unlock unlimited documents and premium features." />

      <div className="mb-8 flex justify-center">
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1">
          {['monthly', 'yearly'].map((c) => (
            <button key={c} onClick={() => setCycle(c)}
              className={`rounded-full px-5 py-1.5 text-sm font-medium capitalize transition-colors ${cycle === c ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-800'}`}>
              {c}{c === 'yearly' && <span className="ml-1.5 text-xs opacity-90">save 17%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Free plan */}
        <Card className="flex flex-col p-6">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><Sparkles className="h-5 w-5" /></div>
          <h3 className="text-lg font-bold text-slate-900">Free</h3>
          <p className="mt-1 text-sm text-slate-500">For getting started</p>
          <p className="mt-4 text-3xl font-extrabold text-slate-900">RM 0<span className="text-sm font-medium text-slate-400">/mo</span></p>
          <ul className="mt-6 flex-1 space-y-2.5">
            {FEATURES.free.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
            ))}
          </ul>
          <Button variant="outline" disabled className="mt-6 w-full">Current Plan</Button>
        </Card>

        {/* Paid plans */}
        {plans.map((p) => {
          const meta = planMeta[p.id] || planMeta.pro;
          const Icon = meta.icon;
          const price = cycle === 'monthly' ? p.monthly : p.yearly;
          const lookup = cycle === 'monthly' ? p.monthly_lookup : p.yearly_lookup;
          const busy = loadingKey === lookup;
          return (
            <Card key={p.id} className={`relative flex flex-col p-6 ${meta.highlight ? 'ring-2 ring-blue-600' : ''}`}>
              {meta.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-0.5 text-xs font-semibold text-white">Most Popular</span>}
              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-lg ${meta.tint}`}><Icon className="h-5 w-5" /></div>
              <h3 className="text-lg font-bold text-slate-900">{p.name.replace('BizDocs ', '')}</h3>
              <p className="mt-1 text-sm text-slate-500">{p.id === 'pro' ? 'For growing businesses' : 'For established teams'}</p>
              <p className="mt-4 text-3xl font-extrabold text-slate-900">RM {price}<span className="text-sm font-medium text-slate-400">/{cycle === 'monthly' ? 'mo' : 'yr'}</span></p>
              <ul className="mt-6 flex-1 space-y-2.5">
                {(FEATURES[p.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <Button onClick={() => checkout(lookup)} disabled={busy}
                className={`mt-6 w-full ${meta.highlight ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'}`}>
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting...</> : `Upgrade to ${p.name.replace('BizDocs ', '')}`}
              </Button>
            </Card>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-slate-400">Secure checkout by Stripe · Test mode · Use card 4242 4242 4242 4242, any future expiry & CVC.</p>
    </div>
  );
}
